// ============================================================
// dimension.js — MOTOR DE DIMENSIÓN: cuánto ocupa cada CAJA (la de la imagen y la del texto).
//
// Gemelo de `posicion.js`, y por el mismo motivo vive en `utils/` y no en `editor/`: lo
// consumen las DOS orillas —el RENDER del bloque, que pinta el tamaño, y el gesto del editor,
// que lo calcula— y un bloque no puede importar de `js/editor/` sin arriesgar el ciclo
// registry→bloque (gotcha nº3). Módulo PURO (sin DOM, sin estado, ES2019 → node-testeable).
//
// ── DOS CAJAS QUE SE MUEVEN SOLAS (v4) ──
// El modelo es el de Canva de verdad: **cada caja se redimensiona sola** y la vecina no se entera
// salvo que físicamente no quepa. Lo pidió el cliente al probar el divisor de la v3 ("ese agarre
// lo quiero para achicar el texto, no para mover la frontera"): mover la frontera cambiaba el
// ancho de la foto a la vez que el del texto, que es justo lo que no se esperaba.
//
//   · `text_width`   (30..100 %) = ancho de la CAJA DEL TEXTO dentro de SU columna. Anclada al
//     borde EXTERIOR de la columna, así que el hueco que suelta queda vacío: la imagen NO se
//     entera. **Cambió de significado en la v4** (ver "EL CAMBIO DE SIGNIFICADO" abajo).
//   · `media_width`  (30..100 %) = ancho de la CAJA DE LA IMAGEN dentro de su columna, anclada
//     también al borde exterior. En modo 'marco' (`imagen` v2) no hay columna: es el ancho del
//     marco respecto a la sección, y ahí sigue CENTRADA.
//   · `media_height` (120..900 px) = ALTO de esa caja (`object-fit:cover`). En px y no en %
//     porque un alto en % de la fila no tiene contra qué resolverse: la fila mide lo que miden
//     sus hijos.
//   · `media_scale`  (25..75 %)  = REPARTO de columnas. Ya NO se arrastra en el lienzo (el
//     divisor desapareció): es un mando avanzado del panel, y lo escribe además el EMPUJE de la
//     imagen cuando ésta pide más ancho del que su columna tiene (`repartoDesdeAncho`).
//
// Ausentes ⇒ el bloque se pinta EXACTAMENTE como antes de existir esta feature (ni variables ni
// clases), así que ningún sitio ya generado cambia un píxel.
//
// ── LA IMAGEN EMPUJA AL TEXTO; EL TEXTO NUNCA A LA IMAGEN ──
// Arrastrar la caja del texto solo mueve la caja del texto. Arrastrar la de la imagen produce un
// ancho deseado `W` en píxeles que `repartoDesdeAncho` reparte en DOS props de forma
// DETERMINISTA e INVERTIBLE: mientras la foto cabe en su columna solo cambia `media_width`;
// cuando pide más, la columna CRECE (y el texto cede) con `media_width` al 100 %. Al encoger se
// vuelve por el mismo camino, sin histéresis, porque el resultado depende solo de `W`.
//
// ── EL CAMBIO DE SIGNIFICADO DE `text_width` (v3 → v4) ──
// Hasta la v3, `text_width` era un LEGADO que `normalizarDimension` convertía al reparto (era la
// otra mitad de la misma frontera). En la v4 es un grado de libertad PROPIO: el ancho de la caja
// del texto. La conversión desaparece; lo único que sobrevive es la identidad de `media_scale` y
// el `media_scale` → `media_width` de `imagen` v2 (donde el viejo prop ya era el ancho del marco).
// Los sitios guardados con `text_width` durante los dos días de la v3 (pre-producción) se leerán
// como "caja de texto más estrecha": se ve, no rompe nada, y "Restablecer" lo deshace.
//
// POR QUÉ EL REPARTO ES `flex` Y NO `width`: las dos columnas viven en un `display:flex` con
// `flex-wrap` y su apilado de móvil está resuelto con reglas `!important` sobre `flex`/`min-width`
// (site.css: `@container (max-width:768px)`). Un `width:35%` inline le ganaría a esas reglas y
// dejaría la foto a un tercio de pantalla en un móvil. Repartiendo por `flex-grow`, el responsive
// sigue funcionando gratis. El ancho de cada CAJA sí es `width`, pero dentro de su columna.
//
// SEGURIDAD: el número acaba interpolado en un atributo `style=` del `<section>`, que NO pasa por
// `cssTokenSafe` ni por el saneador de publicación. Por eso `escalaSegura`/`altoSeguro` son la
// ÚNICA puerta: devuelven un número finito y acotado o `null`, nunca la cadena que venga en props.
// ============================================================

/** Rango del REPARTO de columnas (% del ancho repartible que se lleva la columna de la imagen).
 *  Fuera de 25..75 una de las dos columnas deja de ser una columna. */
export var SPLIT_MIN = 25;
export var SPLIT_MAX = 75;

/** Rango del ANCHO DE LA CAJA DE LA IMAGEN dentro de su columna (o de la sección, en 'marco'). */
export var ANCHO_MIN = 30;
export var ANCHO_MAX = 100;

/** Rango del ANCHO DE LA CAJA DEL TEXTO dentro de su columna. Mismo criterio que el de la caja de
 *  la imagen: por debajo del 30 % una columna de texto deja de poder leerse. */
export var TEXTO_MIN = 30;
export var TEXTO_MAX = 100;

/**
 * Rango del ALTO de la caja, en PÍXELES. El suelo (120) es donde una foto deja de leerse como
 * foto; el techo (900) es algo más que una pantalla de portátil — por encima, la sección deja de
 * caber y el cliente ya tiene la variante a sangre de `imagen` para eso.
 */
export var ALTO_MIN = 120;
export var ALTO_MAX = 900;

/** Imán de MAYÚS en el REPARTO: los repartos "de catálogo" (tercios, mitades, áureos). */
export var PASOS = [25, 33, 40, 50, 60, 66, 75];

/** Imán de MAYÚS en el ANCHO DE UNA CAJA: su rango llega al 100 (la caja puede llenar su hueco).
 *  Lo comparten la caja de la imagen y la del texto: son la misma clase de medida. */
export var PASOS_ANCHO = [30, 40, 50, 60, 66, 75, 80, 90, 100];

/** Imán de MAYÚS en el eje Y. En px no hay "repartos de catálogo": se encaja a una rejilla. */
export var PASO_ALTO = 20;

/** Los 8 lados de las asas de la CAJA (Canva): laterales, superior/inferior y esquinas. En modo
 *  'split' solo se emiten los del lado INTERIOR (ver `asasCaja`): el borde exterior está clavado
 *  al padding de la sección y un asa ahí no movería nada. */
export var LADOS_CAJA = ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se'];

/** Nombres de las variables CSS. Los consumen el render (las emite) y site.css (las lee). */
export var VAR_MEDIA_FLEX = '--media-flex';
export var VAR_TEXT_FLEX = '--text-flex';
export var VAR_MEDIA_W = '--media-w';
export var VAR_MEDIA_H = '--media-h';
export var VAR_TEXT_W = '--text-w';

/** Clases del CONTRATO DE MARKUP: encienden el CSS genérico del motor (site.css). Solo se
 *  emiten cuando hay prop — sin ellas el CSS no toca nada. */
export var CLASE_DIM = 'n3i-dim';        // hay REPARTO (⇒ --media-flex y --text-flex viajan)
export var CLASE_DIM_W = 'n3i-dim-w';    // hay ANCHO de la caja de la imagen (⇒ --media-w)
export var CLASE_DIM_H = 'n3i-dim-h';    // hay ALTO de la caja de la imagen (⇒ --media-h)
export var CLASE_DIM_T = 'n3i-dim-t';    // hay ANCHO de la caja del TEXTO (⇒ --text-w)

/**
 * TODOS los props que este motor escribe o lee. Lo usan "Restablecer" (borra la lista entera: la
 * ausencia de prop es lo que devuelve el bloque a su natural) y el punto de "tocado a mano" del
 * botón del panel. Una lista y no cuatro comprobaciones sueltas: al añadir un eje, el botón de
 * restablecer se enteraba el último.
 * NOTA para `ai-merge.js`: ninguno está en el schema del LLM a propósito — son ajustes de
 * maqueta hechos con la mano, y la unión sobre el original los conserva.
 */
export var PROPS_DIMENSION = ['media_scale', 'media_width', 'media_height', 'text_width', 'split_auto'];

/**
 * QUÉ BLOQUE (y en qué VARIANTE) se puede redimensionar a mano. Es una LISTA DE CAPACIDAD de las
 * de la checklist 5.4 bis: si un bloque no está aquí, no pierde nada visible — simplemente no le
 * salen las asas ni el botón del panel, en silencio. Por eso se declara UNA vez y la leen los
 * renders, el editor y `tests/_front_sources.py::tipos_redimensionables`.
 *
 *   modo 'split' → fila de DOS columnas: caja del texto + caja de la imagen, cada una anclada al
 *                  borde EXTERIOR de su columna. El reparto entre columnas es un mando del panel.
 *   modo 'marco' → UNA caja CENTRADA en la sección: solo el ancho de la caja.
 *   ancho        → ¿la caja de la imagen tiene ancho propio (`media_width`)?
 *   alto         → ¿tiene alto propio (`media_height`, asas n/s + esquinas)?
 *   grow         → REPARTO NATURAL del bloque, expresado como el `flex-grow` de la MEDIA con el
 *                  texto en 1 (solo importa el cociente): en `content` la columna de la foto vale
 *                  1.15 por site.css, en `hero` 1. De aquí sale la BASE del empuje
 *                  (`repartoNatural`): el reparto desde el que la imagen empieza a empujar.
 *
 * `imagen` v2 se queda SIN eje Y a propósito: su alto ya lo gobierna el selector de altura del
 * bloque (`data-altura` auto/compacto/medio/alto), que es un modelo distinto —un TECHO al que la
 * foto encoge entera— y superponerle un alto libre daría dos mandos peleando por lo mismo.
 *
 * Fuera del alcance a propósito: los fondos CSS (hero v1/v2, banner v2) no tienen caja que medir;
 * `carrusel` y `ai-custom` no se tocan nunca. ADOPTARLO EN OTRO BLOQUE es una línea aquí + las
 * clases del contrato de markup + `asasCaja()`/`asaTexto()` en su render: ni CSS ni JS nuevos
 * (receta en CODEBASE_SUMMARY, PARTE 5).
 *
 * OJO al tocar el literal: `tests/_front_sources.py::tipos_redimensionables` lo PARSEA con una
 * expresión regular que espera `tipo: { variantes: [ … ]` — conserva esa forma.
 */
export var BLOQUES_REDIMENSIONABLES = {
    content: { variantes: ['v1', 'v3'], modo: 'split', ancho: true, alto: true, grow: 1.15 },
    hero: { variantes: ['v3'], modo: 'split', ancho: true, alto: true, grow: 1 },
    imagen: { variantes: ['v2'], modo: 'marco', ancho: true, alto: false, grow: 1 },
};

/**
 * ¿Este tipo+variante admite redimensionado manual? → su FICHA, o `null`. La ficha es el único
 * argumento que necesitan `normalizarDimension`/`estiloDimension`/`clasesDimension`: ningún
 * render decide nada por su cuenta.
 * @returns {{modo:string, ancho:boolean, alto:boolean, grow:number}|null}
 */
export function dimensionesDe(tipo, variante) {
    var ficha = BLOQUES_REDIMENSIONABLES[tipo];
    if (!ficha) return null;
    var v = String(variante || 'v1');
    if (ficha.variantes.indexOf(v) < 0) return null;
    return {
        modo: ficha.modo === 'marco' ? 'marco' : 'split',
        ancho: !!ficha.ancho,
        alto: !!ficha.alto,
        grow: isFinite(Number(ficha.grow)) && Number(ficha.grow) > 0 ? Number(ficha.grow) : 1,
    };
}

// ── LA CAJA DE TEXTO ÚNICA (modo 'caja', 26-09-2026) ──────────────────────────────────────
// La rama de UNA columna de un bloque —sin imagen al lado— también tiene una caja de texto
// (titular + párrafo + CTA) que el cliente puede ensanchar o estrechar: el hero con la foto de
// FONDO (v1, v2, v4, la v3 sin media y las variantes antiguas), el `content` sin imagen y el
// `banner`. Aquí no hay columnas: ni reparto, ni empuje, ni eje Y. Solo el ANCHO de la caja, que
// se queda donde el bloque la pone: centrada (crece por los dos lados, asas `e` y `w`) o, en las
// variantes ANCLADAS, pegada al oeste (el panel del hero v4: solo el asa `e`).
//
// PROP PROPIO, `text_box_width`, y no `text_width` (dueño, 26-09): el de la fila es el % de SU
// COLUMNA y éste el % del TECHO de la sección, dos números distintos. Compartiéndolo, el
// `text_width` que un hero v3 dejó al cambiar de variante se aplicaba de golpe a la caja única
// (el v4 del sitio 17 de test222 arrastra un 37,3). Así, cada modo conserva lo suyo al ir y venir
// de variante, y «Restablecer» borra solo lo del modo que se está viendo.
//
// EL 100 % ES EL TECHO de la caja (dueño, 26-09):
//   'seccion'   → el ancho de contenido de la sección. El hero está fuera de «Ancho del
//                 contenido» aunque traiga `content_width` (el aplicador de --cw lo excluye).
//   'contenido' → el «Ancho del contenido» (`--cw`: 720/1080/100 %) si la sección lo lleva; si no,
//                 la sección. El ajuste es el techo y el asa estrecha DENTRO de él.
//
// SIN PROP, LO PUBLICADO SALE IDÉNTICO BYTE A BYTE: la clase de la sección, su variable y la
// clase de la caja solo se emiten con prop, y las asas son `.editor-only` (`compileSiteHtml` las
// borra sin dejar rastro: van pegadas al último hijo, sin espacio propio).

/**
 * Qué bloque tiene caja de texto ÚNICA. No enumera variantes a propósito: la decide la RAMA de una
 * columna del render, sea cual sea la variante (las antiguas, `centered`/`primary`, caen en ella).
 * Solo se nombran las ANCLADAS, que sí tienen que existir (test_registro_bloques).
 * OJO al tocar el literal: `tests/_front_sources.py::tipos_caja_unica` lo parsea con la forma
 * `tipo: { techo: '…', ancladas: [ … ]`.
 */
export var CAJA_TEXTO_UNICA = {
    hero: { techo: 'seccion', ancladas: ['v4'] },
    content: { techo: 'contenido', ancladas: [] },
    banner: { techo: 'contenido', ancladas: [] },
};

export var PROP_CAJA_UNICA = 'text_box_width';
/** Los props del modo 'caja': lo que su «Restablecer» borra y lo que enciende su punto de «tocado». */
export var PROPS_CAJA_UNICA = [PROP_CAJA_UNICA];
/** FRACCIÓN del techo (0,3..1), sin unidad: el CSS la multiplica por el techo, `calc(techo * k)`. */
export var VAR_CAJA_K = '--caja-k';
export var CLASE_DIM_U = 'n3i-dim-u';          // en la <section>: hay ANCHO de la caja única
export var CLASE_CAJA_UNICA = 'n3i-caja-unica'; // en la caja (solo con prop)

/**
 * Ficha de la caja única del bloque, o `null`. La llama el render DESDE la rama de una columna y
 * el editor desde el asa (`data-asa="unica"`): ninguno decide por su cuenta si la caja existe.
 * @returns {{modo:'caja', techo:'seccion'|'contenido', lados:string[]}|null}
 */
export function cajaUnicaDe(tipo, variante) {
    var ficha = CAJA_TEXTO_UNICA[tipo];
    if (!ficha) return null;
    var anclada = (ficha.ancladas || []).indexOf(String(variante || 'v1')) >= 0;
    return {
        modo: 'caja',
        techo: ficha.techo === 'contenido' ? 'contenido' : 'seccion',
        lados: anclada ? ['e'] : ['e', 'w'],
    };
}

/** El ancho guardado (% del techo, 30..100) o `null` si no hay. Única puerta del número al CSS. */
export function anchoCajaUnica(p) {
    return escalaSegura(p ? p[PROP_CAJA_UNICA] : null, TEXTO_MIN, TEXTO_MAX, null);
}

/**
 * Lo que emite el render de UNA pasada: `clases` y `estilo` para el `<section>` y `claseCaja`
 * para la caja. Cadenas vacías sin ficha o sin prop (⇒ HTML idéntico al de antes).
 */
export function atributosCajaUnica(p, ficha) {
    var t = ficha ? anchoCajaUnica(p) : null;
    if (t === null) return { clases: '', estilo: '', claseCaja: '' };
    return {
        clases: ' ' + CLASE_DIM_U,
        estilo: ' ' + VAR_CAJA_K + ':' + (Math.round(t * 10) / 1000) + ';',
        claseCaja: CLASE_CAJA_UNICA,
    };
}

/**
 * Normaliza lo que venga en props a un porcentaje usable. **Nunca lanza.**
 * @param {*} v          valor crudo de props
 * @param {number} min   suelo del rango
 * @param {number} max   techo del rango
 * @param {*} porDefecto qué devolver si `v` no sirve (por defecto `null` = "no hay valor")
 */
export function escalaSegura(v, min, max, porDefecto) {
    var pd = porDefecto === undefined ? null : porDefecto;
    // Solo un NÚMERO o una cadena con algo dentro. `Number()` a pelo colaba `true` (→ 1),
    // `' '` (→ 0) y `[50]` (→ 50) como medidas reales: un prop corrupto (import, merge de la
    // varita) dejaba de ser "ausente" y encendía una clase `n3i-dim*` que nadie pidió.
    if (typeof v === 'string') { if (!v.trim()) return pd; }
    else if (typeof v !== 'number') return pd;
    var n = Number(v);
    if (!isFinite(n)) return pd;
    var lo = isFinite(Number(min)) ? Number(min) : 0;
    var hi = isFinite(Number(max)) ? Number(max) : 100;
    n = Math.max(lo, Math.min(hi, n));
    return Math.round(n * 10) / 10;
}

/**
 * Paso del imán más cercano a un porcentaje (lo que hace MAYÚS mientras se arrastra). La LISTA
 * es un argumento porque los ejes en % tienen catálogos distintos: el REPARTO no llega al
 * 100 (dejaría una columna sin ancho) y el ancho de una caja sí.
 */
export function imanAPasos(v, pasos) {
    var lista = (pasos && pasos.length) ? pasos : PASOS;
    var n = Number(v);
    if (!isFinite(n)) return lista[0];
    return lista.reduce(function (mejor, p) {
        return Math.abs(p - n) < Math.abs(mejor - n) ? p : mejor;
    }, lista[0]);
}

/**
 * Alto en PÍXELES ya acotado (o `null` si no hay valor). Hermano de `escalaSegura` para el eje Y,
 * con su propio rango y redondeo a píxel ENTERO: un `--media-h:317.4px` no aporta nada y ensucia
 * el HTML publicado.
 */
export function altoSeguro(v, porDefecto) {
    var pd = porDefecto === undefined ? null : porDefecto;
    var n = escalaSegura(v, ALTO_MIN, ALTO_MAX, null);
    return n === null ? pd : Math.round(n);
}

/** Imán de MAYÚS en el eje Y: rejilla de `PASO_ALTO` px, dentro del rango. */
export function imanAlto(v) {
    var n = Number(v);
    if (!isFinite(n)) return ALTO_MIN;
    return altoSeguro(Math.round(n / PASO_ALTO) * PASO_ALTO, ALTO_MIN);
}

/**
 * ESQUINA = escala PROPORCIONAL (lo que hace Canva al agarrar un vértice): el desplazamiento del
 * puntero se PROYECTA sobre la diagonal de la caja y de ahí sale un único factor `k` que
 * multiplica ancho y alto a la vez.
 *
 *     k = 1 + (dx·sx + dy·sy) / (w0 + h0)
 *
 * El denominador es el perímetro de media caja, no la hipotenusa, a propósito: con `w0 + h0` un
 * arrastre puro en X y otro puro en Y del mismo número de píxeles dan el MISMO `k`, así que la
 * esquina se siente igual de "rápida" se mueva por donde se mueva. `sx`/`sy` son los signos del
 * lado agarrado (+1 = ese borde crece al alejarse del centro).
 *
 * Función PURA (sin DOM): la parte medible del gesto vive aquí y no en editor/redimensionar.js.
 *
 * @returns {{k:number, ancho:number, alto:number}} `ancho` en px SIN acotar (quien llama lo
 *   convierte a % contra su propio total y lo acota ahí); `alto` ya pasado por `altoSeguro`.
 */
export function escalaEsquina(w0, h0, dx, dy, sx, sy) {
    var W = Number(w0), H = Number(h0);
    if (!isFinite(W) || W <= 0) W = 1;
    if (!isFinite(H) || H <= 0) H = 1;
    var x = isFinite(Number(dx)) ? Number(dx) : 0;
    var y = isFinite(Number(dy)) ? Number(dy) : 0;
    var ex = Number(sx) > 0 ? 1 : (Number(sx) < 0 ? -1 : 0);
    var ey = Number(sy) > 0 ? 1 : (Number(sy) < 0 ? -1 : 0);
    var k = 1 + (x * ex + y * ey) / (W + H);
    if (!isFinite(k) || k < 0.05) k = 0.05;
    k = Math.round(k * 1000) / 1000;
    return { k: k, ancho: W * k, alto: altoSeguro(H * k, ALTO_MIN) };
}

/**
 * El REPARTO NATURAL del bloque en %, derivado de su `grow` (que es el mismo número que su CSS
 * aplica de verdad): `content` 1.15 → 53,5 % · `hero` 1 → 50 %. Es la BASE del empuje: mientras la
 * caja de la imagen quepa en la columna que el bloque le da de serie, el reparto no se toca.
 */
export function repartoNatural(ficha) {
    var g = (ficha && isFinite(Number(ficha.grow)) && Number(ficha.grow) > 0) ? Number(ficha.grow) : 1;
    return escalaSegura(100 * g / (g + 1), SPLIT_MIN, SPLIT_MAX, 50);
}

/**
 * ¿El reparto guardado lo puso un EMPUJE (automático) o el cliente en el panel (elegido)?
 * Un empuje deja `media_scale` + `split_auto:true`; el panel deja `media_scale` a secas (y
 * borra la marca). Es lo que hace que encoger una caja DESHAGA el empuje: la base del gesto
 * siguiente vuelve a ser el natural y la frontera regresa sola — sin marca, el reparto empujado
 * se quedaba como base y el cliente tenía que ir al panel a recomponer el 50/50 a mano.
 */
export function repartoEsAutomatico(p) {
    return !!(p && p.split_auto === true);
}

/**
 * La BASE contra la que se mide el empuje: el reparto que el cliente ELIGIÓ en el panel
 * (`media_scale` sin `split_auto`) o, si no lo ha tocado —o si lo que hay lo puso un empuje
 * anterior—, el natural del bloque. Un solo sitio lo decide para que el gesto, el panel y los
 * tests no puedan discrepar.
 */
export function baseDelReparto(p, ficha) {
    var s = (p && !!ficha && ficha.modo !== 'marco' && !repartoEsAutomatico(p))
        ? escalaSegura(p.media_scale, SPLIT_MIN, SPLIT_MAX, null) : null;
    return s === null ? repartoNatural(ficha) : s;
}

/**
 * El reparto que SE VE, medido en pantalla: ancho de la columna de la imagen contra el ancho
 * repartible. Es la base del empuje cuando el cliente no ha fijado `media_scale`, y NO el
 * natural de la ficha: `editor/unsplash.js` pone `flex:1.2` a la columna del texto al resolver
 * la foto, así que el reparto real de content es ~48,9 %, no 53,5 % — con la base teórica el
 * gesto pintaba una cosa y al soltar se guardaba otra (salto de ~9 %). Sin medida → `porDefecto`.
 */
export function repartoMedido(anchoMedia, total, porDefecto) {
    var t = Number(total), a = Number(anchoMedia);
    if (!isFinite(t) || t <= 0 || !isFinite(a) || a < 0) return porDefecto;
    return escalaSegura(100 * a / t, SPLIT_MIN, SPLIT_MAX, porDefecto);
}

/**
 * El TECHO del reparto que el layout permite de verdad: la columna del texto lleva un
 * `min-width` inline (300/320 px) y flexbox no baja de ahí — por encima, la etiqueta y el prop
 * prometerían un ancho que la foto no tendría. Nunca por debajo de `base` (la caja siempre
 * puede quedarse donde está).
 */
export function techoDelReparto(total, minTexto, base) {
    var t = Number(total), m = Number(minTexto), b = Number(base);
    if (!isFinite(b)) b = SPLIT_MIN;
    if (!isFinite(t) || t <= 0) return Math.max(b, SPLIT_MAX);
    if (!isFinite(m) || m < 0) m = 0;
    var techo = Math.floor((t - m) / t * 1000) / 10;
    return Math.max(b, Math.min(SPLIT_MAX, techo));
}

/**
 * EL EMPUJE, en una función PURA (y por eso node-testeable). Recibe el ancho DESEADO de la caja
 * de la imagen en píxeles y devuelve los dos props que lo consiguen:
 *
 *     split       = clamp(max(base, W/T·100), 25, 75)
 *     media_width = min(100, W / (T·split/100) · 100)
 *
 * Leído en castellano: mientras la foto quepa en su columna (la que le da `base`), la columna no
 * se toca y solo cambia el ancho de la caja; en cuanto pide MÁS, la columna crece justo lo
 * necesario y la caja la llena (100 %). Es DETERMINISTA —el resultado depende solo de `W`— así
 * que encoger deshace exactamente lo que agrandar hizo: no hay histéresis posible.
 *
 * @param {number} W  ancho deseado de la caja, en px
 * @param {number} T  ancho REPARTIBLE de la fila (las dos columnas sin el hueco), en px
 * @param {number} base reparto de partida en % (ver `baseDelReparto`)
 * @param {'media'|'texto'} [lado] qué caja empuja (por defecto la imagen). Con 'texto' la
 *   regla es la ESPEJO: el reparto baja cuando el texto pide más que su columna (100 − split).
 *   `ancho` vuelve entonces en la escala de `text_width`.
 * @returns {{split:?number, ancho:?number}} listos para props/pintado, o nulos si `T` no sirve
 */
export function repartoDesdeAncho(W, T, base, lado) {
    var t = Number(T);
    if (!isFinite(t) || t <= 0) return { split: null, ancho: null };
    var b = escalaSegura(base, SPLIT_MIN, SPLIT_MAX, 50);
    var w = Number(W);
    if (!isFinite(w) || w < 0) w = 0;
    var q = 100 * w / t;                       // ancho de la caja en % del total repartible
    if (lado === 'texto') {
        // SIMÉTRICO: la caja del TEXTO empuja la frontera hacia la imagen. Su columna es
        // (100 − split); mientras la caja quepa, el reparto no se toca; cuando pide más, el
        // reparto BAJA (la imagen cede) justo lo necesario y la caja llena su columna.
        var splitT = escalaSegura(Math.min(b, 100 - q), SPLIT_MIN, SPLIT_MAX, b);
        var anchoT = escalaSegura(100 * q / (100 - splitT), TEXTO_MIN, TEXTO_MAX, TEXTO_MAX);
        return { split: splitT, ancho: anchoT };
    }
    var split = escalaSegura(Math.max(b, q), SPLIT_MIN, SPLIT_MAX, b);
    var ancho = escalaSegura(100 * q / split, ANCHO_MIN, ANCHO_MAX, ANCHO_MAX);
    return { split: split, ancho: ancho };
}

/**
 * La INVERSA de `repartoDesdeAncho`: qué mide en píxeles una caja con ese reparto y ese ancho.
 * La usa el gesto para arrancar (convertir la medida de partida a la unidad del arrastre) y los
 * tests para comprobar la ida y vuelta.
 */
export function anchoDesdeReparto(split, ancho, T, lado) {
    var t = Number(T);
    var s = escalaSegura(split, SPLIT_MIN, SPLIT_MAX, 50);
    if (!isFinite(t) || t <= 0) return 0;
    if (lado === 'texto') {
        var aT = escalaSegura(ancho, TEXTO_MIN, TEXTO_MAX, TEXTO_MAX);
        return t * ((100 - s) / 100) * (aT / 100);
    }
    var a = escalaSegura(ancho, ANCHO_MIN, ANCHO_MAX, ANCHO_MAX);
    return t * (s / 100) * (a / 100);
}

/**
 * EL PUNTO ÚNICO donde los props se convierten en medidas. Devuelve los cuatro grados de libertad
 * ya acotados (o `null` = "este bloque no lo tiene / el cliente no lo ha tocado"). Lo llaman el
 * render (para emitir estilo y clases) y el editor (para sembrar los sliders): si cada uno leyera
 * props por su cuenta, el sitio pintado y el panel dirían números distintos, que es exactamente el
 * síntoma que cierra este motor.
 *
 * ÚNICA conversión que queda (v4): en modo 'marco' el viejo `media_scale` de `imagen` v2 ERA el
 * ancho del marco, o sea lo que hoy es `media_width` — mismo dato, nombre nuevo. La conversión de
 * `text_width` al reparto desapareció con el divisor: ahora es el ancho de la caja del texto.
 *
 * @param {object} p     props del bloque (ya pasados por `safe()` en el render)
 * @param {object} ficha lo que devuelve `dimensionesDe`
 * @returns {{split:?number, ancho:?number, alto:?number, texto:?number}}
 */
export function normalizarDimension(p, ficha) {
    var out = { split: null, ancho: null, alto: null, texto: null };
    if (!p || !ficha) return out;
    var esMarco = ficha.modo === 'marco';

    // ── ANCHO DE LA CAJA DE LA IMAGEN ──
    if (ficha.ancho) {
        var w = escalaSegura(p.media_width, ANCHO_MIN, ANCHO_MAX, null);
        // LEGADO de `imagen` v2 (ver arriba): el prop nuevo manda si están los dos.
        if (w === null && esMarco) w = escalaSegura(p.media_scale, ANCHO_MIN, ANCHO_MAX, null);
        out.ancho = w;
    }

    // ── ALTO DE LA CAJA DE LA IMAGEN ──
    if (ficha.alto) out.alto = altoSeguro(p.media_height);

    // ── REPARTO Y CAJA DEL TEXTO (solo donde hay dos columnas) ──
    if (!esMarco) {
        out.split = escalaSegura(p.media_scale, SPLIT_MIN, SPLIT_MAX, null);
        out.texto = escalaSegura(p.text_width, TEXTO_MIN, TEXTO_MAX, null);
    }
    return out;
}

/**
 * El trozo de `style` que el `<section>` del bloque emite. Punto ÚNICO donde el número se
 * convierte en CSS: los renders llaman aquí y ninguno interpola a mano (si alguno lo hiciera, se
 * saltaría el clamp — ver la nota de seguridad de arriba).
 *
 * Las dos variables del reparto salen del MISMO número y son complementarias: con `flex-basis:0`
 * el reparto es exactamente s:(100−s), así que el slider ya no puede mentir.
 * Sin props ⇒ cadena vacía ⇒ HTML idéntico al de antes de existir esta feature.
 */
export function estiloDimension(p, ficha) {
    return _estiloDe(normalizarDimension(p, ficha));
}

function _estiloDe(d) {
    var out = '';
    if (d.split !== null) {
        var resto = Math.round((100 - d.split) * 10) / 10;
        out += ' ' + VAR_MEDIA_FLEX + ':' + d.split + '; ' + VAR_TEXT_FLEX + ':' + resto + ';';
    }
    if (d.ancho !== null) out += ' ' + VAR_MEDIA_W + ':' + d.ancho + '%;';
    if (d.alto !== null) out += ' ' + VAR_MEDIA_H + ':' + d.alto + 'px;';
    if (d.texto !== null) out += ' ' + VAR_TEXT_W + ':' + d.texto + '%;';
    return out;
}

/**
 * Las clases que ENCIENDEN el CSS genérico, con su espacio delante para concatenarlas en el
 * `class=` del `<section>`. Solo se emiten cuando hay valor, y ésa es la razón de que existan:
 * el CSS del motor consume `var(--media-flex)` SIN fallback útil, así que una clase puesta sin su
 * variable dejaría al bloque con el reparto por defecto del motor en vez de con el suyo.
 */
export function clasesDimension(p, ficha) {
    return _clasesDe(normalizarDimension(p, ficha));
}

function _clasesDe(d) {
    var c = [];
    if (d.split !== null) c.push(CLASE_DIM);
    if (d.ancho !== null) c.push(CLASE_DIM_W);
    if (d.alto !== null) c.push(CLASE_DIM_H);
    if (d.texto !== null) c.push(CLASE_DIM_T);
    return c.length ? ' ' + c.join(' ') : '';
}

/**
 * Lo que el `<section>` de un bloque redimensionable emite, de UNA sola pasada: `clases` (para
 * su `class=`) y `estilo` (para su `style=`). Es lo que llaman los renders — antes llamaban a
 * `clasesDimension` y a `estiloDimension` por separado y cada una volvía a sanear los props,
 * en el camino caliente del editor (cada `renderPage` repinta todos los bloques).
 * Con `ficha` nula (el bloque no se redimensiona) devuelve cadenas vacías.
 */
export function atributosDimension(p, ficha) {
    if (!ficha) return { clases: '', estilo: '' };
    var d = normalizarDimension(p, ficha);
    return { clases: _clasesDe(d), estilo: _estiloDe(d) };
}

/** Texto humano de lo que mueve cada lado de la caja de la imagen (va al `title` del asa). */
function _queMueve(lado) {
    if (lado === 'n' || lado === 's') return 'el alto de la imagen';
    if (lado === 'e' || lado === 'w') return 'el ancho de la imagen';
    return 'el tamaño de la imagen';
}

/**
 * UN asa (chrome de EDICIÓN). Se emite SIEMPRE —nunca gateada por `editorMode`— y marcada
 * `.editor-only`: `compileSiteHtml` la borra al publicar y
 * `body:not(.editor-active) .editor-only{display:none}` la esconde en el borrador servido en
 * vivo. Emitir de más es seguro; emitir de menos, no (corolario del gotcha nº12).
 *
 * `tabindex="-1"`: es un gesto de PUNTERO puro (arrastrar), sin acción de teclado propia. La vía
 * accesible es el panel de precisión del `.block-tools` (sliders manejables con las flechas), así
 * que meterlas en el orden de tabulación solo añadiría paradas mudas.
 *
 * Ninguna lleva `on*` (invariante nº13): el gesto va por delegación de clase.
 */
function _asa(rol, lado, clase, titulo, etiqueta, dentro) {
    return '<button class="' + clase + ' n3i-asa editor-only" type="button" tabindex="-1"'
        + ' data-asa="' + rol + '" data-lado="' + lado + '"'
        + ' title="' + titulo + '" aria-label="' + etiqueta + '">' + (dentro || '') + '</button>';
}

/** Los puntos de agarre de una píldora (SVG, nunca un emoji): dicen "esto se arrastra". */
function _puntos() {
    return '<svg viewBox="0 0 12 30" width="10" height="24" fill="currentColor" aria-hidden="true">'
        + '<circle cx="4" cy="9" r="1.4"/><circle cx="8" cy="9" r="1.4"/>'
        + '<circle cx="4" cy="15" r="1.4"/><circle cx="8" cy="15" r="1.4"/>'
        + '<circle cx="4" cy="21" r="1.4"/><circle cx="8" cy="21" r="1.4"/></svg>';
}

/**
 * Asa de la CAJA DE LA IMAGEN: el cuadradito con borde de acento de cualquier editor visual.
 * El LADO dice qué hace el arrastre — `e`/`w` = ancho · `n`/`s` = alto · esquinas = escala
 * proporcional— y es además de donde el gesto saca los SIGNOS, así que no hay que adivinar la
 * orientación midiendo la caja. No lleva SVG a propósito: es una FORMA, no un icono (la pinta
 * site.css con `--accent`, el color de marca de la sección).
 */
export function asaCaja(lado) {
    var l = LADOS_CAJA.indexOf(String(lado || '')) >= 0 ? String(lado) : 'e';
    return _asa('caja', l,
        'n3i-asa-caja',
        'Arrastra para cambiar ' + _queMueve(l) + ' (mantén Mayús para encajarlo en pasos)',
        'Redimensionar la imagen');
}

/**
 * Las asas de la CAJA DE LA IMAGEN, en un solo string y hijas directas de `.n3i-caja`. Se llama
 * desde el render para que la lista viva en UN sitio: si cada bloque las enumerara, añadir un lado
 * obligaría a tocar tres ficheros y el que se olvidara perdería ese eje **en silencio**.
 *
 * La FICHA filtra los ejes que el bloque no tiene (en modo 'marco' solo hay e/w: su alto lo
 * gobierna `data-altura`), y el LADO INTERIOR filtra los bordes que no pueden moverse: en una fila
 * de dos columnas la caja va anclada al borde EXTERIOR (clavado al padding de la sección), así que
 * solo se arrastran la lateral INTERIOR, `n`, `s` y las dos esquinas de ese lado — **5**. Un asa en
 * el borde clavado no movería un píxel, y eso es peor que no tenerla (gotcha 42: en la v2 la `e` de
 * la imagen movía la frontera de enfrente).
 *
 * @param {object} ficha        lo que devuelve `dimensionesDe`
 * @param {'e'|'w'} [ladoInterior]  el borde que da al texto. Ausente ⇒ los 8 lados que permita la
 *   ficha (modo 'marco', donde la caja está centrada y los dos laterales se mueven).
 */
export function asasCaja(ficha, ladoInterior) {
    var conAncho = !ficha || ficha.ancho !== false;
    var conAlto = !ficha || ficha.alto !== false;
    var interior = ladoInterior === 'e' ? 'e' : (ladoInterior === 'w' ? 'w' : null);
    return LADOS_CAJA.filter(function (l) {
        var esX = l === 'e' || l === 'w';
        var esY = l === 'n' || l === 's';
        if (esX) return conAncho && (!interior || l === interior);
        if (esY) return conAlto;
        // Esquinas: mueven los dos ejes a la vez, y solo las del lado que puede moverse.
        return conAncho && conAlto && (!interior || l.indexOf(interior) >= 0);
    }).map(asaCaja).join('');
}

/**
 * EL ASA DE LA CAJA DEL TEXTO: una píldora en su borde INTERIOR (el que da a la imagen), que mueve
 * SOLO el texto. Sustituye al divisor de la v3, que movía la frontera y con ella el ancho de la
 * foto — "ese agarre lo quiero para achicar el texto, no para tocar la imagen" (cliente, PDR-0k).
 * El espacio que la caja suelta queda VACÍO entre el texto y la imagen: la vecina no se entera.
 *
 * @param {'e'|'w'} lado  el borde INTERIOR de la columna del TEXTO. Lo sabe el render, que conoce
 *   la variante (content v3 invierte la fila con `row-reverse`), y es además de donde el gesto saca
 *   el signo: no se adivina midiendo.
 */
export function asaTexto(lado) {
    var l = (String(lado || 'e') === 'w') ? 'w' : 'e';
    return _asa('texto', l,
        'n3i-asa-texto',
        'Arrastra para cambiar el ancho del texto (mantén Mayús para encajarlo en pasos)',
        'Cambiar el ancho del texto',
        _puntos());
}

/**
 * Las asas de la CAJA DE TEXTO ÚNICA, en un solo string, para pegarlas como ÚLTIMOS hijos de la
 * caja sin espacio delante (así `compileSiteHtml` las borra sin dejar rastro). Misma píldora que
 * la del texto de la fila (`n3i-asa-texto`) más `n3i-asa-unica`, que es de donde el CSS saca su
 * visibilidad y el editor el modo: `data-asa="unica"`. Una por cada lado de la ficha: dos en una
 * caja centrada, solo la `e` en una anclada al oeste.
 */
export function asasCajaUnica(ficha) {
    if (!ficha || !ficha.lados) return '';
    return ficha.lados.map(function (l) {
        return _asa('unica', l === 'w' ? 'w' : 'e',
            'n3i-asa-texto n3i-asa-unica',
            'Arrastra para cambiar el ancho del texto (mantén Mayús para encajarlo en pasos)',
            'Cambiar el ancho del texto',
            _puntos());
    }).join('');
}
